import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient, useQuery, useMutation, useIsFetching } from '@tanstack/react-query';
import { User, Clock, Plus, Trash2, Loader2 } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { workOrdersService } from '../../services/workOrdersService';
import { assetService } from '../../../../services/activos/assetService';
import { useEmployees } from '../../../personas/empleados/hooks/useEmployees';
import { laborScheduleService } from '../../../personas/calendario/services/laborScheduleService';
import { extCompanyService } from '../../services/extCompanyService';

// Help convert timestamps to minutes for representation in 24h timeline overlay
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (m || 0);
};

// Analyze schedule to determine availability and 24h timeline overlay representation
const analyzeSchedule = (schedule, workOrder) => {
  const blocks = [];
  let isAvailable = false;
  let hasWoTime = false;

  if (schedule?.loading || schedule?.error || (!schedule?.current && !schedule?.previous)) {
    return { isAvailable: null, blocks };
  }

  let woStart = 0, woEnd = 0;
  if (workOrder?.scheduled_date && workOrder?.estimated_hours != null) {
    const timeStr = workOrder.scheduled_date.split('T')[1];
    if (timeStr) {
      woStart = timeToMinutes(timeStr.slice(0, 5));
      const duration = parseFloat(workOrder.estimated_hours) * 60;
      woEnd = woStart + duration;
      hasWoTime = true;

      if (duration > 0) {
        blocks.push({
          id: 'wo',
          startMin: woStart,
          endMin: Math.min(woStart + duration, 1440),
          label: 'Work Order',
          color: 'bg-amber-500/70',
          borderColor: 'border-amber-400',
          zIndex: 5
        });
      }
    }
  }

  // Previous Day Overlap
  if (schedule.previous?.IsWorkingDay && schedule.previous.StartTime && schedule.previous.EndTime) {
    const pStart = timeToMinutes(schedule.previous.StartTime);
    const pEnd = timeToMinutes(schedule.previous.EndTime);
    if (pEnd <= pStart) {
      blocks.push({
        id: 'prev',
        startMin: 0,
        endMin: pEnd,
        label: schedule.previous.ShiftCode.charAt(0).toUpperCase() + schedule.previous.ShiftCode.slice(1) || 'Prev Shift',
        color: 'bg-indigo-500/50',
        borderColor: 'border-indigo-400'
      });
      if (hasWoTime && woStart < pEnd) {
        isAvailable = true;
      }
    }
  }

  // Current Day Shift
  if (schedule.current?.IsWorkingDay && schedule.current.StartTime && schedule.current.EndTime) {
    const cStart = timeToMinutes(schedule.current.StartTime);
    const cEnd = timeToMinutes(schedule.current.EndTime);
    if (cEnd <= cStart) {
      blocks.push({
        id: 'curr',
        startMin: cStart,
        endMin: 1440,
        label: schedule.current.ShiftCode.charAt(0).toUpperCase() + schedule.current.ShiftCode.slice(1) || 'Shift',
        color: 'bg-blue-500/50',
        borderColor: 'border-blue-400'
      });
      if (hasWoTime && woEnd > cStart) {
        isAvailable = true;
      }
    } else {
      blocks.push({
        id: 'curr',
        startMin: cStart,
        endMin: cEnd,
        label: schedule.current.ShiftCode.charAt(0).toUpperCase() + schedule.current.ShiftCode.slice(1) || 'Shift',
        color: 'bg-blue-500/50',
        borderColor: 'border-blue-400'
      });
      if (hasWoTime && ((woStart >= cStart && woStart < cEnd) || (woEnd <= cEnd && woEnd > cStart))) {
        isAvailable = true;
      }
    }
  }

  return { isAvailable: hasWoTime ? isAvailable : null, blocks };
};

// Render 24h Timeline Overlay
const ScheduleTimeline = ({ blocks }) => {
  return (
    <div className="mt-4">
      <h5 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">24h Timeline Overlay</h5>
      <div className="relative h-12 bg-gray-900 rounded border border-gray-700 overflow-hidden">
        {[0, 6, 12, 18, 24].map((hour) => (
          <div key={hour} className="absolute top-0 bottom-0 border-l border-gray-700/50" style={{ left: `${(hour / 24) * 100}%` }}>
            <span className="absolute text-[10px] text-gray-500 top-1 left-1">{hour.toString().padStart(2, '0')}:00</span>
          </div>
        ))}
        {blocks.map((block) => (
          <div
            key={block.id}
            className={`absolute top-2 bottom-2 rounded text-[10px] font-semibold text-white px-1 py-1 truncate border flex items-center ${block.color} ${block.borderColor}`}
            style={{
              left: `${(block.startMin / 1440) * 100}%`,
              width: `${((block.endMin - block.startMin) / 1440) * 100}%`,
              zIndex: block.zIndex || 1
            }}
            title={`${block.label} (${Math.floor(block.startMin / 60)}:${(block.startMin % 60).toString().padStart(2, '0')} - ${Math.floor(block.endMin / 60) === 24 ? 24 : Math.floor(block.endMin / 60)}:${(block.endMin % 60).toString().padStart(2, '0')})`}
          >
            {block.label}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-400">
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-indigo-500/50 border border-indigo-400"></div> Prev Day Overlap</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500/50 border border-blue-400"></div> Current Day Shift</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-500/70 border border-amber-400"></div> Work Order</div>
      </div>
    </div>
  );
};

export const WorkOrdersAssignments = ({ onOpen, workOrder, dateFormat }) => {
  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ queryKey: ['wo'] });
  const workOrderId = workOrder?.id;
  const assignments = workOrder?.assignments || [];
  const assignments_external = workOrder?.assignments_external || [];


  const [isAdding, setIsAdding] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    employee_id: '',
    role: '',
  });

  const [assignmentType, setAssignmentType] = useState('internal');
  const [externalCompany, setExternalCompany] = useState('');
  const [externalContract, setExternalContract] = useState('');
  const [currentContractIndex, setCurrentContractIndex] = useState(0);

  const handleTypeChange = (type) => {
    if (type !== assignmentType) {
      setAssignmentType(type);
      setNewAssignment(prev => ({
        ...prev,
        employee_id: '' // Unselect employee when switching types
      }));
      setExternalCompany(''); // Reset selected company
      setExternalContract(''); // Reset selected contract
      setCurrentContractIndex(0); // Reset carousel
      setShiftFilter(null); // Reset shift filter for internal
    }
  };

  const [shiftFilter, setShiftFilter] = useState(null);
  const [addError, setAddError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeSchedule, setEmployeeSchedule] = useState({ current: null, previous: null, loading: false, error: null, currentDate: '', previousDate: '' });
  const [filteredByShift, setFilteredByShift] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);

  // 1. Hook para gestión de empleados NUESTROS , de COEXPAN / EMSUR/ LANTERO
  const employeesHook = useEmployees();
  const {
    employees,
    loading,
    error,
  } = employeesHook;

  // 2. Obtenemos el ID de la fábrica a la que pertenece el activo de la OT.
  const { data: assetHierarchyHook } = useQuery({
    queryKey: ['asset-hierarchy', workOrder?.asset_id],
    queryFn: () => assetService.getAssetHierarchy(workOrder?.asset_id),
    enabled: !!workOrder?.asset_id,
  });
  const assetHierarchy = assetHierarchyHook || null;

  const { data: parentCodes = [] } = useQuery({
    queryKey: ['asset-parent-codes', assetHierarchy?.ID],
    queryFn: async () => {
      if (!assetHierarchy?.ID) return [];
      const ids = [assetHierarchy.ID];
      if (assetHierarchy.ParentID) {
        ids.push(assetHierarchy.ParentID);
      }

      let currentParentId = assetHierarchy.ParentID;
      while (currentParentId) {
        try {
          const parent = await assetService.getAssetHierarchy(currentParentId);
          if (parent?.ParentID && !ids.includes(parent.ParentID)) {
            ids.push(parent.ParentID);
            currentParentId = parent.ParentID;
          } else {
            break;
          }
        } catch (error) {
          console.error("Error fetching parent asset hierarchy:", error);
          break;
        }
      }
      return ids;
    },
    enabled: !!assetHierarchy?.ID,
  });
  // Obtener contratos de la empresa seleccionada y filtrar por jerarquía de activos
  const { data: extContractsHook } = useQuery({
    queryKey: ['ext-contracts', Number(externalCompany)],
    queryFn: () => extCompanyService.getMaintenanceContracts({ company_id: Number(externalCompany), active: true }, 1, 1000),
    enabled: !!externalCompany,
  });

  const availableContracts = useMemo(() => {
    if (!extContractsHook?.data) return [];
    return extContractsHook.data.filter(contract =>
      !contract.asset_id || parentCodes.includes(contract.asset_id)
    );
  }, [extContractsHook, parentCodes]);

  // 3. Fetcheamos las empresas y nos quedamos solo con aquellas cuyas fábricas contengan la del activo de la OT.
  const { data: extCompaniesHook } = useQuery({
    queryKey: ['ext-companies'],
    queryFn: () => extCompanyService.getAllExtCompanies(true, 1, 1000)
  });

  const extCompanies = extCompaniesHook || { data: [], total: 0 };

  const availableCompanies = useMemo(() => {
    return extCompanies?.data?.filter(comp => comp?.factories?.includes(parentCodes?.at(-1)));
  }, [parentCodes, extCompanies])

  console.log(Number(externalCompany))

  // 4. Obtenemos todos los empleados de la empresa seleccionada
  const { data: extEmployeesHook } = useQuery({
    queryKey: ['ext-employees', Number(externalCompany)],
    queryFn: () => extCompanyService.getExtEmployeeByExtCompany(externalCompany, 1, 1000, true),
    enabled: !!externalCompany,
  })
  const extEmployees = extEmployeesHook?.data || [];

  //Shift Filter 
  useEffect(() => {
    //if there is no shift filter, return all employees
    if (!shiftFilter) {
      setFilteredByShift(employees || []);
      return;
    }

    const fetchAndFilter = async () => {
      setIsFiltering(true);
      try {
        //Get the work order date
        const woDateStr = workOrder?.scheduled_date?.split('T')[0];
        //If employee list empty or work order has no scheduled date, cancel filter.
        if (!woDateStr || !employees?.length) {
          setFilteredByShift(employees || []);
          return;
        }
        //Fetch employee schedules for the work order date
        const results = await Promise.all(
          employees.map(async (employee) => {
            try {
              const schedule = await laborScheduleService.getEmployeeDaySchedule(employee.id, woDateStr);
              return { employee, schedule };
            } catch (err) {
              return { employee, schedule: null };
            }
          })
        );
        //filter employees by shift
        const filtered = results
          .filter(r => r.schedule?.ShiftCode?.toLowerCase() === shiftFilter.toLowerCase())
          .map(r => r.employee);

        setFilteredByShift(filtered);
      } catch (err) {
        console.error("Error filtering employees by shift", err);
        setFilteredByShift(employees || []);
      } finally {
        setIsFiltering(false);
      }
    };

    fetchAndFilter();
  }, [shiftFilter, employees, workOrder?.scheduled_date]);

  //Get employee schedule for workorder day and previous day.
  useEffect(() => {
    if (!newAssignment.employee_id || !workOrder?.scheduled_date || assignmentType !== 'internal') {
      setEmployeeSchedule({ current: null, previous: null, loading: false, error: null, currentDate: '', previousDate: '' });
      return;
    }

    const fetchSchedule = async () => {
      setEmployeeSchedule(prev => ({ ...prev, loading: true, error: null }));
      try {
        const woDateStr = workOrder.scheduled_date.split('T')[0]; // "YYYY-MM-DD"

        const woDateObj = new Date(woDateStr);
        // previous day
        const prevDateObj = new Date(woDateObj);
        prevDateObj.setDate(woDateObj.getDate() - 1);
        const prevDateStr = prevDateObj.toISOString().split('T')[0];

        const [currentDay, prevDay] = await Promise.all([
          laborScheduleService.getEmployeeDaySchedule(newAssignment.employee_id, woDateStr),
          laborScheduleService.getEmployeeDaySchedule(newAssignment.employee_id, prevDateStr)
        ]);

        setEmployeeSchedule({
          current: currentDay,
          previous: prevDay,
          currentDate: woDateStr,
          previousDate: prevDateStr,
          loading: false,
          error: null
        });
      } catch (err) {
        setEmployeeSchedule(prev => ({ ...prev, loading: false, error: 'Failed to load schedule' }));
      }
    };
    fetchSchedule();
  }, [newAssignment.employee_id, workOrder?.scheduled_date]);

  //Refresh work orders
  const invalidateWorkOrders = () => {
    queryClient.invalidateQueries({ queryKey: ['wo'] });
  };

  //Mutations
  const addMutation = useMutation({
    mutationFn: (data) => workOrdersService.addAssig(workOrderId, data),
    onSuccess: () => {
      invalidateWorkOrders();
      setIsAdding(false);
      setNewAssignment({ employee_id: '', role: '' });
      setAddError('');
      setIsSubmitting(false);
    },
    onError: (err) => setAddError(err.message || 'Error adding assignment'),
  });

  const addExtMutation = useMutation({
    mutationFn: (data) => workOrdersService.addExtAssig(workOrderId, data),
    onSuccess: () => {
      invalidateWorkOrders();
      setIsAdding(false);
      setNewAssignment({ employee_id: '', role: '' });
      setExternalCompany('');
      setExternalContract('');
      setAddError('');
      setIsSubmitting(false);
    },
    onError: (err) => setAddError(err.message || 'Error adding assignment'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => workOrdersService.deleteAssig(id),
    onSuccess: () => invalidateWorkOrders(),
  });

  const deleteExtMutation = useMutation({
    mutationFn: (id) => workOrdersService.deleteExtAssig(id),
    onSuccess: () => invalidateWorkOrders(),
  });

  //Handles
  const handleAdd = () => {
    setAddError('')
    console.log('Assigning employee...');
    if (assignmentType === 'external') {
      if (!externalCompany) {
        setAddError('External company is required');
        setIsSubmitting(false);
        return;
      }
      if (!externalContract) {
        setAddError('Contract is required');
        setIsSubmitting(false);
        return;
      }
      if (!newAssignment.employee_id) {
        setAddError('External employee is required');
        setIsSubmitting(false);
        return;
      }
      if (!newAssignment.role.trim()) {
        setAddError('Role is required');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        company_id: parseInt(externalCompany),
        contract_id: parseInt(externalContract),
        employee_id: parseInt(newAssignment.employee_id),
        role: newAssignment.role.trim(),
      };
      if (assignments_external.find(ass => ass.employee_id === payload.employee_id)) {
        setAddError('Employee already assigned to work order');
        console.log('Duplicate employee');
        setIsSubmitting(false);
        return;
      }
      addExtMutation.mutate(payload);
      return;
    }
    if (!newAssignment.employee_id) {
      setAddError('Employee is required');
      setIsSubmitting(false);
      return;
    }
    console.log('Id: OK');
    if (!newAssignment.role.trim()) {
      setAddError('Role is required');
      setIsSubmitting(false);
      return;
    }
    console.log('Role: OK');
    const payload = {
      employee_id: parseInt(newAssignment.employee_id),
      role: newAssignment.role.trim(),
    };
    console.log('Loaded: OK');
    if (assignments.find(ass => ass.employee_id === payload.employee_id)) {
      setAddError('Employee already assigned to work order');
      console.log('Duplicate employee');
      setIsSubmitting(false);
      return;
    }
    console.log('All good');
    addMutation.mutate(payload);
  };

  const handleDelete = (id) => {

    if (window.confirm('Remove this assignment?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleExtDelete = (id) => {

    if (window.confirm('Remove this assignment?')) {
      deleteExtMutation.mutate(id);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    handleAdd();
  };


  const { isAvailable, blocks: scheduleBlocks } = useMemo(() => {
    return analyzeSchedule(employeeSchedule, workOrder);
  }, [employeeSchedule, workOrder]);

  if (!workOrderId) return null;
  if (isFetching > 0)
    return (
      <Loader2 className="animate-spin w-5 h-5 text-blue-400" />
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <User className="w-5 h-5 text-gray-400" />
          Assignments
          <Badge className="bg-gray-700 text-gray-300">
            {assignments.length + assignments_external.length} {assignments.length + assignments_external.length === 1 ? 'assignment' : 'assignments'}
          </Badge>
        </h3>
        <button
          onClick={() => {
            setAddError('');
            setAssignmentType('internal');
            setExternalCompany('');
            setNewAssignment({ employee_id: '', role: '' });
            setShiftFilter(null);
            setIsAdding(true);
          }}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 rounded-lg text-white"
        >
          <Plus className="w-4 h-4" /> Add Assignment
        </button>

      </div>

      {/* Add form */}
      {isAdding && (
        <form onSubmit={handleFormSubmit} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
          <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-600/50">
            <span className="text-sm font-semibold text-gray-300">Assignment Type</span>
            <div className="flex bg-gray-800 p-0.5 rounded-lg border border-gray-600">
              <button
                type="button"
                onClick={() => handleTypeChange('internal')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${assignmentType === 'internal'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Internal
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('external')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${assignmentType === 'external'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                External
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {assignmentType === 'internal' ? (
              <div className="flex items-center flex-wrap gap-3">
                <select
                  value={newAssignment.employee_id}
                  onChange={(e) => setNewAssignment({ ...newAssignment, employee_id: e.target.value })}
                  className="px-3 py-2 flex-1 min-w-[200px] truncate rounded bg-gray-800 text-white border border-gray-600"
                  disabled={isFiltering}
                >
                  <option value="">{isFiltering ? 'Filtering...' : 'Select employee'}</option>
                  {filteredByShift.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name && emp.last_name
                        ? `${emp.first_name} ${emp.last_name}`
                        : emp.first_name || emp.last_name || 'N/A'}
                    </option>
                  ))}
                </select>

                <select
                  value={shiftFilter}
                  onChange={(e) => setShiftFilter(e.target.value)}
                  className="px-3 py-2 flex-1 min-w-0 truncate rounded bg-gray-800 text-white border border-gray-600"
                >
                  <option value="">Select shift</option>
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="night">Night</option>
                </select>
              </div>
            ) : (
              <div className="flex items-center flex-wrap gap-3">
                <select
                  value={externalCompany}
                  onChange={(e) => {
                    setExternalCompany(e.target.value);
                    setExternalContract('');
                    setCurrentContractIndex(0);
                    setNewAssignment(prev => ({ ...prev, employee_id: '' }));
                  }}
                  className="px-3 py-2 flex-1 min-w-[200px] truncate rounded bg-gray-800 text-white border border-gray-600"
                >
                  <option value="">Select company</option>
                  {availableCompanies?.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>

                {/* Contract Carousel/Grid */}
                {externalCompany && (
                  <div className="w-full mt-2">
                    <p className="text-xs text-gray-400 mb-2">Select Maintenance Contract</p>
                    {availableContracts.length === 0 ? (
                      <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-900/50 rounded-lg p-2">No valid contracts found for this company and asset hierarchy.</p>
                    ) : (
                      <div className="space-y-3">
                        {(() => {
                          const contract = availableContracts[currentContractIndex];
                          if (!contract) return null;
                          const isSelected = externalContract === contract.id.toString() || externalContract === contract.id;

                          let isExpiringSoon = false;
                          try {
                            if (contract.end_date) {
                              const end = new Date(contract.end_date).getTime();
                              const diffDays = (end - Date.now()) / (1000 * 60 * 60 * 24);
                              isExpiringSoon = diffDays > 0 && diffDays <= 60;
                            }
                          } catch { }

                          return (
                            <div
                              onClick={() => setExternalContract(contract.id.toString())}
                              className={`cursor-pointer rounded-xl p-4 border transition-all ${isSelected
                                ? 'bg-blue-900/30 border-blue-500 shadow-md ring-1 ring-blue-500/50'
                                : 'bg-gray-700/30 border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                                }`}
                            >
                              <div className="flex justify-between items-start gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <h4 className="text-white font-semibold text-base">
                                      {contract.contract_number}
                                    </h4>
                                    <Badge className={`text-xs px-2 py-0.5 border ${contract.contract_type === 'preventive' ? 'bg-blue-900/50 text-blue-200 border-blue-500/30' :
                                      contract.contract_type === 'corrective' ? 'bg-amber-900/50 text-amber-200 border-amber-500/30' :
                                        contract.contract_type === 'full_service' ? 'bg-purple-900/50 text-purple-200 border-purple-500/30' :
                                          'bg-gray-700 text-gray-300 border-gray-500/30'
                                      }`}>
                                      {contract.contract_type === 'preventive' ? 'Preventive' :
                                        contract.contract_type === 'corrective' ? 'Corrective' :
                                          contract.contract_type === 'full_service' ? 'Full Service' :
                                            contract.contract_type || 'Standard'}
                                    </Badge>
                                    <Badge className={contract.active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}>
                                      {contract.active ? 'Active' : 'Inactive'}
                                    </Badge>
                                    {isExpiringSoon && (
                                      <Badge className="bg-amber-900/50 text-amber-200 border border-amber-500/30">
                                        Expiring Soon
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-blue-400 font-medium capitalize mt-2 mb-1">
                                    Service: {contract.service_type?.replace(/_/g, ' ') || contract.contract_type}
                                  </p>
                                  <p className="text-xs text-gray-400 truncate">
                                    {contract.asset_id ? 'Asset Specific' : 'General Contract'}
                                  </p>
                                </div>
                                <div className="shrink-0 flex items-center h-full pt-1">
                                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-blue-400 bg-blue-500' : 'border-gray-500'}`}>
                                    {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                                  </div>
                                </div>
                              </div>

                              {/* Quick stats grid */}
                              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                                <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                                  <span className="text-gray-400 block mb-0.5">Validity</span>
                                  <span className="text-gray-200 font-medium">
                                    {dateFormat(contract.start_date).split(' ')[0]} - {dateFormat(contract.end_date).split(' ')[0]}
                                  </span>
                                </div>
                                <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                                  <span className="text-gray-400 block mb-0.5">Annual Cost</span>
                                  <span className="text-emerald-400 font-semibold">
                                    {contract.annual_cost !== undefined && contract.annual_cost !== null
                                      ? `${Number(contract.annual_cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                                      : 'N/A'}
                                  </span>
                                </div>
                                <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                                  <span className="text-gray-400 block mb-0.5">SLA / Hours</span>
                                  <span className="text-gray-200 font-medium">
                                    {contract.response_time ? `${contract.response_time}h response` : 'N/A'} • {contract.service_hours || '24/7'}
                                  </span>
                                </div>
                                <div className="bg-gray-800/60 p-2.5 rounded-lg border border-gray-750">
                                  <span className="text-gray-400 block mb-0.5">Scope</span>
                                  <span className="text-gray-200 font-medium truncate block">
                                    {contract.asset_code ? `Asset: ${contract.asset_code}` : contract.factory_name ? `Factory: ${contract.factory_name}` : 'General / All'}
                                  </span>
                                </div>
                              </div>

                              {/* Scope Services Details */}
                              {(contract.included_services || contract.excluded_services) && (
                                <div className="mt-3 pt-3 border-t border-gray-700/60 space-y-2 text-xs">
                                  {contract.included_services && (
                                    <div>
                                      <span className="text-green-400 font-medium">Included Services:</span>
                                      <p className="text-gray-300 mt-0.5 whitespace-pre-wrap bg-gray-800/40 p-2 rounded border border-gray-750 max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600">
                                        {contract.included_services}
                                      </p>
                                    </div>
                                  )}
                                  {contract.excluded_services && (
                                    <div>
                                      <span className="text-red-400 font-medium">Excluded Services:</span>
                                      <p className="text-gray-300 mt-0.5 whitespace-pre-wrap bg-gray-800/40 p-2 rounded border border-gray-750 max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600">
                                        {contract.excluded_services}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Dots */}
                        {availableContracts.length > 1 && (
                          <div className="flex justify-center gap-2 py-1">
                            {availableContracts.map((_, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentContractIndex(idx);
                                }}
                                className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentContractIndex ? 'bg-blue-400' : 'bg-gray-600 hover:bg-gray-400'}`}
                                aria-label={`Go to contract ${idx + 1}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <select
                  value={newAssignment.employee_id}
                  onChange={(e) => setNewAssignment({ ...newAssignment, employee_id: e.target.value })}
                  className="px-3 py-2 flex-1 min-w-[200px] truncate rounded bg-gray-800 text-white border border-gray-600"
                  disabled={!externalCompany}
                >
                  <option value="">
                    {!externalCompany ? 'Select a company first' : 'Select employee'}
                  </option>
                  {externalCompany && extEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <input
              type="text"
              placeholder="Role *"
              maxLength="100"
              value={newAssignment.role}
              onChange={(e) => setNewAssignment({ ...newAssignment, role: e.target.value.slice(0, 100) })}
              className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600"
            />
          </div>

          {newAssignment.employee_id && assignmentType === 'internal' && (
            <div className="mt-4 bg-gray-800 p-3 rounded-lg border border-gray-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-300">Employee Availability</h4>
                {isAvailable === true && (
                  <Badge className="bg-green-900/50 text-green-200">Available</Badge>
                )}
                {isAvailable === false && (
                  <Badge className="bg-red-900/50 text-red-200">Unavailable</Badge>
                )}
              </div>
              {employeeSchedule.loading ? (
                <div className="flex items-center text-sm text-gray-400 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading schedule...
                </div>
              ) : employeeSchedule.error ? (
                <div className="text-sm text-red-400">{employeeSchedule.error}</div>
              ) : (
                <div className="space-y-3 text-sm">
                  {employeeSchedule.previous && (
                    <div className="flex justify-between items-center bg-gray-700/30 p-2 rounded">
                      <span className="text-gray-400">Previous Day ({employeeSchedule.previousDate})</span>
                      {employeeSchedule.previous.IsWorkingDay ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-900/50 text-blue-200">
                            {employeeSchedule.previous.ShiftCode.charAt(0).toUpperCase() + employeeSchedule.previous.ShiftCode.slice(1) || 'Working'}
                          </Badge>
                          <span className="text-white">
                            {employeeSchedule.previous.StartTime} - {employeeSchedule.previous.EndTime}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">Day Off {employeeSchedule.previous.Reason ? `(${employeeSchedule.previous.Reason})` : ''}</span>
                      )}
                    </div>
                  )}
                  {employeeSchedule.current && (
                    <div className="flex justify-between items-center bg-gray-700/30 p-2 rounded">
                      <span className="text-gray-400">WO Day ({employeeSchedule.currentDate})</span>
                      {employeeSchedule.current.IsWorkingDay ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-900/50 text-blue-200">
                            {employeeSchedule.current.ShiftCode.charAt(0).toUpperCase() + employeeSchedule.current.ShiftCode.slice(1) || 'Working'}
                          </Badge>
                          <span className="text-white">
                            {employeeSchedule.current.StartTime} - {employeeSchedule.current.EndTime}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">Day Off {employeeSchedule.current.Reason ? `(${employeeSchedule.current.Reason})` : ''}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!employeeSchedule.loading && !employeeSchedule.error && (
                <ScheduleTimeline blocks={scheduleBlocks} />
              )}
            </div>
          )}

          {addError && <p className="text-red-400 text-sm mt-3">{addError}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className="text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            <button type="submit" className="px-3 py-1 bg-blue-600 rounded-lg hover:bg-blue-700  disabled:opacity-50 disabled:cursor-not-allowed text-white inline-flex items-center gap-2"
              disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      )}

      {/* Assignment list - read only */}
      <div className="space-y-3">
        {assignments.map((ass) => (
          <div key={ass.id} className="bg-gray-700/30 rounded-lg border border-gray-600 p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-white font-medium">{ass.employee_name}</p>
                {ass.role && <p className="text-sm text-gray-400">Role: {ass.role}</p>}
              </div>
              <button
                onClick={() => handleDelete(ass.id)}
                className="text-gray-400 hover:text-red-400"
                title="Delete assignment"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {ass.hours_worked > 0 && (
              <div className="mt-2 text-sm text-gray-300">
                <Clock className="w-3 h-3 inline mr-1" />
                {ass.hours_worked} hours worked
              </div>
            )}
            {(ass.start_time || ass.end_time) && (
              <div className="mt-2 text-xs text-gray-500">
                {ass.start_time && <span>Started: {dateFormat(ass.start_time)}</span>}
                {ass.end_time && <span className="ml-3">Ended: {dateFormat(ass.end_time)}</span>}
              </div>
            )}
          </div>
        ))}
        {assignments_external.map((ass) => (
          <div key={ass.id} className="bg-gray-700/30 rounded-lg border border-gray-600 p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="">
                <p className="text-white font-medium">{ass.employee_name}<span className="ml-2"><Badge className="text-xs h-4 w-auto relative font-normal bg-blue-900/20 border border-blue-300 text-blue-200">{ass.company_name}</Badge></span></p>
                {ass.role && <p className="text-sm text-gray-400">Role: {ass.role}</p>}
                {ass.contract_number && <p className="text-sm text-gray-400">Contract: {ass.contract_number}</p>}
              </div>
              <button
                onClick={() => handleExtDelete(ass.id)}
                className="text-gray-400 hover:text-red-400"
                title="Delete assignment"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {ass.hours_worked > 0 && (
              <div className="mt-2 text-sm text-gray-300">
                <Clock className="w-3 h-3 inline mr-1" />
                {ass.hours_worked} hours worked
              </div>
            )}
            {(ass.start_time || ass.end_time) && (
              <div className="mt-2 text-xs text-gray-500">
                {ass.start_time && <span>Started: {dateFormat(ass.start_time)}</span>}
                {ass.end_time && <span className="ml-3">Ended: {dateFormat(ass.end_time)}</span>}
              </div>
            )}
          </div>
        ))}
        {assignments.length === 0 && assignments_external.length === 0 && !isAdding && (
          <div className="text-center py-8 text-gray-400">
            <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No assignments yet. Click "Add Assignment" to create one.</p>
          </div>
        )}
      </div>
    </div>
  );
};